/**
 * A picture the signer picked or took (an ID proof), made small enough to send: at most 2000 px on its
 * long side, as a JPEG, turned the right way up. A phone photo of 4–12 MB becomes a few hundred KB.
 * Where the browser can't decode pictures (tests, very old browsers), a JPEG or PNG is sent as it is.
 */
export const MAX_SEND_BYTES = 2_800_000;
const LONG_SIDE = 2000;

export class PictureError extends Error {
  constructor(readonly code: "not_image" | "too_large" | "unreadable") {
    super(code);
    this.name = "PictureError";
  }
}

/** Bytes a base64 data URL decodes to. */
export const dataUrlBytes = (dataUrl: string) => Math.floor(((dataUrl.length - dataUrl.indexOf(",") - 1) * 3) / 4);

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new PictureError("unreadable"));
    r.readAsDataURL(file);
  });
}

const jpgName = (name: string) => (name.replace(/\.[^.]+$/, "") || "picture") + ".jpg";

export async function preparePicture(file: File): Promise<{ dataUrl: string; name: string }> {
  if (file.type && !file.type.startsWith("image/")) throw new PictureError("not_image");
  if (typeof createImageBitmap === "function" && typeof document !== "undefined") {
    let bmp: ImageBitmap | null = null;
    try {
      bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bmp = null;
    }
    if (bmp) {
      const scale = Math.min(1, LONG_SIDE / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bmp.width * scale));
      canvas.height = Math.max(1, Math.round(bmp.height * scale));
      const g = canvas.getContext("2d");
      if (g) {
        g.drawImage(bmp, 0, 0, canvas.width, canvas.height);
        bmp.close?.();
        for (const q of [0.85, 0.7, 0.55]) {
          const dataUrl = canvas.toDataURL("image/jpeg", q);
          if (dataUrl.startsWith("data:image/jpeg") && dataUrlBytes(dataUrl) <= MAX_SEND_BYTES) return { dataUrl, name: jpgName(file.name) };
        }
        throw new PictureError("too_large");
      }
    }
  }
  if (!/^image\/(jpeg|png)$/.test(file.type)) throw new PictureError("unreadable");
  if (file.size > MAX_SEND_BYTES) throw new PictureError("too_large");
  return { dataUrl: await readAsDataUrl(file), name: file.name || "picture" };
}
